import { supabase } from 'src/clientLib/supabase'
import { RootState } from 'src/redux/root'
import { ubemIoApi } from 'src/redux/services/ubem'
import { setError } from 'src/redux/slices/error/reducers'
import { UpdateUser, User, UserInOrganization } from 'src/types/database-model'

import { PostgrestError } from '@supabase/supabase-js'

import { UpdateUserInOrgArg, UpdateUserInOrgRes } from './types'

export const mutationFixedCacheKeys = {}

const workflow = 'users'

interface IdAndOrgId {
  id: number
  organization_id: number
}
interface EmailAndOrganization {
  email: string
  organization_id: number
}

interface CreateOrganizationArg {
  title: string
}

// TODO: Add queryFulfilled error handlers and

export const usersApi = ubemIoApi.injectEndpoints({
  endpoints: (builder) => ({
    acceptInvite: builder.mutation<Omit<UserInOrganization, 'user_id'>[] | null, IdAndOrgId>({
      queryFn: async ({ id, organization_id }, { getState }) => {
        const state = getState() as RootState
        const user_id = state.user.session?.user.id
        if (user_id == null) throw { message: 'No user selected.' }
        const result = await supabase.from('invitations').update({ accepted: true }).eq('id', id)
        if (result.error) throw result.error
        const { data, error } = await supabase
          .from('users_in_organizations')
          .insert({
            user_id,
            organization_id,
            access_role: 'Editor',
            sustainability_role: 'Energy Modeler',
          })
          .select()
        if (error) throw error
        return { data }
      },
      invalidatesTags: (result, query, { organization_id }) => {
        return [
          "User's Organizations",
          "User's Pending Invites",
          { type: "Org's Pending Invites", id: organization_id },
        ]
      },
    }),
    getPendingInvites: builder.query({
      queryFn: async (args: '', { getState }) => {
        const state = getState() as RootState
        const email = state.user.session?.user.email
        const { data, error } = await supabase
          .from('invitations')
          .select(
            `
                    id,
                    organization_id:organizations(title, id),
                    accepted
                `,
          )
          .eq('email', email)
          .eq('accepted', false)
        if (error) throw error
        return { data }
      },
      providesTags: ["User's Pending Invites"],
    }),
    inviteToOrganization: builder.mutation({
      queryFn: async ({ email, organization_id }: EmailAndOrganization) => {
        const { data, error } = await supabase
          .from('invitations')
          .insert({ email, organization_id })
          .select()
        if (error) throw error
        return { data }
      },
      invalidatesTags: (result, query, { organization_id }) => {
        return [{ type: "Org's Pending Invites", id: organization_id }]
      },
    }),
    createOrganization: builder.mutation({
      queryFn: async ({ title }: CreateOrganizationArg, { getState }) => {
        const state = getState() as RootState
        const creator = state.user.session?.user.id
        const { data, error } = await supabase
          .from('organizations')
          .insert({
            title,
            creator,
          })
          .select()
        if (error) throw error
        const organization_id = data?.[0].id
        if (organization_id == null || creator == null)
          throw { message: 'No user or organization selected' }
        const result = await supabase
          .from('users_in_organizations')
          .insert({
            access_role: 'Admin',
            user_id: creator,
            organization_id,
            sustainability_role: 'Champion',
          })
          .select()
        if (result.error) throw result.error
        return { data: result.data }
      },
      invalidatesTags: ["User's Organizations"],
    }),
    createAuthTokenCookie: builder.mutation({
      // TODO: type this
      query: ({ session }) => {
        return {
          url: 'api/auth/token',
          body: session,
          method: 'POST',
        }
      },
    }),
    updateProfile: builder.mutation<User[] | null, UpdateUser>({
      queryFn: async (arg) => {
        const id = arg.id
        const { data, error } = await supabase.from('users').update(arg).eq('id', id).select()
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow,
              key: 'updateProfile',
            }),
          )
        }
      },
      invalidatesTags: ['Own Profile'],
    }),
    getProfile: builder.query({
      queryFn: async () => {
        // TODO: type this
        const { data, error } = (await supabase.rpc('get_own_profile')) as {
          data: User[] | null
          error: PostgrestError | null
        }
        if (!data) throw { message: 'Could not find user.' }
        if (error) throw error
        return { data: { ...data[0] } }
      },
      providesTags: ['Own Profile'],
    }),
    getOrganizationInvites: builder.query({
      queryFn: async ({ organization_id }: { organization_id: number }) => {
        const { data, error } = await supabase
          .from('invitations')
          .select('email')
          .eq('organization_id', organization_id)
          .eq('accepted', false)
        if (error) throw error
        return { data }
      },
      providesTags: (result, error, { organization_id }) => [
        { type: "Org's Pending Invites", id: organization_id },
      ],
    }),
    getOrganizations: builder.query({
      queryFn: async () => {
        const { data, error } = await supabase.from('organizations').select(`
                    title,
                    organization_id:id,
                    id:users_in_organizations(
                        user_id:users(
                            name,
                            id
                        ),
                        sustainability_role,
                        access_role
                    )`)
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'getOrganizations' }))
        }
      },
      providesTags: ["User's Organizations"],
    }),
    updateUserInOrganization: builder.mutation<UpdateUserInOrgRes, UpdateUserInOrgArg>({
      queryFn: async ({ update, organization_id, user_id }) => {
        console.log(update)
        console.log(organization_id)
        console.log(user_id)
        const { data, error } = await supabase
          .from('users_in_organizations')
          .update(update)
          .eq('organization_id', organization_id)
          .eq('user_id', user_id)
        console.log('here')
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'updateUserInOrganizations' }))
        }
      },
      invalidatesTags: ["User's Organizations"],
    }),
    createProject: builder.mutation({
      queryFn: async ({ title }: { title: string }, { getState }) => {
        const state = getState() as RootState
        const organization_id = state.user.activeOrganization
        const { data, error } = await supabase
          .from('projects')
          .insert({ title, organization_id })
          .select()
        if (error) throw error
        return { data: data }
      },
      invalidatesTags: ['Org Projects'],
    }),
    getProjects: builder.query({
      queryFn: async (_, { getState }) => {
        const state = getState() as RootState
        const organization_id = state.user.activeOrganization
        const { data, error } = await supabase
          .from('projects')
          .select('title, gis_file_location, countryISO3, lat, lon, id, buildingCount')
          .eq('organization_id', organization_id)
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'getProjects' }))
        }
      },
      providesTags: ['Org Projects'],
    }),
  }),
})

export const {
  acceptInvite,
  getPendingInvites,
  inviteToOrganization,
  createOrganization,
  createAuthTokenCookie,
  getProfile,
  updateProfile,
  getOrganizationInvites,
  getOrganizations,
  updateUserInOrganization,
  createProject,
  getProjects,
} = usersApi.endpoints
